import { avatarIndexFromSrc, profileAvatars } from "./profileAvatars";
import { currentUserKey } from "./profileUserStore";

const SESSION_KEY = "profileData";

/**
 * Resolve a displayable profile image from an API/session profile object.
 * Prefers avatar_index → bundled character, then profile_image URL fields.
 */
export function resolveProfileImage(profile) {
  if (!profile || typeof profile !== "object") return "";
  const index = Number(
    profile.avatar_index ?? profile.avatarIndex ?? profile.character_index,
  );
  if (Number.isInteger(index) && index >= 0 && profileAvatars[index]) {
    return profileAvatars[index];
  }
  const fromSrc =
    profile.profileImage ||
    profile.profile_image ||
    profile.avatar ||
    profile.avatar_url ||
    profile.image ||
    "";
  if (typeof fromSrc === "string" && fromSrc.trim()) {
    const matched = avatarIndexFromSrc(fromSrc);
    if (matched >= 0) return profileAvatars[matched];
    return fromSrc.trim();
  }
  return "";
}

function readSessionRaw() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** @deprecated Use resolveProfileImage / session profile from API. */
export function getChosenProfileImage() {
  return resolveProfileImage(readSessionRaw() || {});
}

/** Keep API image as source of truth. */
export function applyChosenProfileImage(profile) {
  if (!profile) return profile;
  const image = resolveProfileImage(profile);
  if (!image) return profile;
  return { ...profile, profileImage: image };
}

export function getChosenProfileImageMeta() {
  const profile = readSessionRaw();
  const src = resolveProfileImage(profile || {});
  if (!src || !String(src).startsWith("data:")) return null;
  return {
    src,
    name: profile?.imageName || "photo.jpg",
    size: Number(profile?.imageSize || 0),
  };
}

/**
 * Optimistic session update for avatar/photo.
 * Caller should also POST /user/profile/complete so the API owns the data.
 */
export function saveChosenProfileImage(src, meta = {}) {
  const image = src || "";
  const avatarIndex = avatarIndexFromSrc(image);
  const key = currentUserKey();
  let previous = readSessionRaw() || {};
  if (previous.userKey && key && previous.userKey !== key) previous = {};

  const next = {
    ...previous,
    profileImage: image,
    avatar_index: avatarIndex >= 0 ? avatarIndex : previous.avatar_index ?? null,
    avatarIndex: avatarIndex >= 0 ? avatarIndex : previous.avatarIndex ?? null,
    imageName: meta?.name || "",
    imageSize: meta?.size ? String(meta.size) : "",
    userKey: key || previous.userKey,
  };

  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event("profileUpdated"));
}

/** No-op: profile avatars are no longer kept in localStorage. */
export function snapshotChosenProfileImage() {
  return null;
}

/** No-op: profile avatars are no longer kept in localStorage. */
export function restoreChosenProfileImage() {
  /* intentionally empty */
}

export function fileToProfileImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not read image"));
      img.onload = () => {
        const max = 480;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        context.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
